// Thin fetch wrapper used by all pages.
// - Sets Content-Type: application/json
// - Sets credentials: 'include' (sends the session cookie)
// - On 401: redirects to /login (unless redirectOn401: false — e.g. the auth-status
//   probe, where a 401 is the normal "logged out" answer and a redirect would loop)
// - On non-ok: parses { error } body and throws an Error with that message
// - On ok: returns parsed JSON body

export async function apiFetch<T = unknown>(
  path: string,
  options?: RequestInit & { redirectOn401?: boolean },
): Promise<T> {
  const { redirectOn401 = true, ...init } = options ?? {};
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    credentials: 'include',
  });

  if (res.status === 401) {
    // Only redirect when the caller wants it. The auth probe (/api/auth/me) opts
    // out: its 401 means "not logged in", handled gracefully by the caller —
    // redirecting there causes an infinite reload loop on the login page.
    if (redirectOn401) window.location.href = '/login';
    throw new Error('Not authenticated');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}
